import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { CareShell, Panel } from "../../components/care/CareLayout";
import { useAuth } from "../../contexts/AuthContext";
import { careService } from "../../services/careService";

const number = (value) => Number(value || 0).toLocaleString();

const formatDate = (value, options = {}) => {
  if (!value) return "Not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not set";
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: options.year === false ? undefined : "numeric",
  });
};

const priorityClass = (priority = "") => String(priority || "Low").toLowerCase();

const statusLabel = (value = "") =>
  String(value || "")
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const metricCards = (metrics = {}) => [
  {
    icon: "ti-users-group",
    label: "Active vehicles",
    value: metrics.activeVehicles,
    note: `${number(metrics.totalVehicles)} total vehicles`,
    tone: "green",
  },
  {
    icon: "ti-clipboard-check",
    label: "Appraisals active",
    value: metrics.activeAppraisals,
    note: `${number(metrics.completedAppraisals)} completed`,
    tone: "blue",
  },
  {
    icon: "ti-calendar-due",
    label: "Campaigns due",
    value: metrics.ncpDueSoon,
    note: "Due within 14 days",
    tone: "amber",
  },
  {
    icon: "ti-clock-hour-4",
    label: "Staff on duty",
    value: metrics.staffOnDuty,
    note: `${number(metrics.upcomingSchedules)} shifts this week`,
    tone: "purple",
  },
];

function EmptyState({ children }) {
  return <div className="care-dashboard-empty">{children}</div>;
}

function MetricCard({ metric }) {
  return (
    <article className={`care-dashboard-metric is-${metric.tone}`}>
      <span className="care-dashboard-metric__icon">
        <i className={`ti ${metric.icon}`} />
      </span>
      <span>
        <small>{metric.label}</small>
        <strong>{number(metric.value)}</strong>
        <em>{metric.note}</em>
      </span>
    </article>
  );
}

function VehicleAttentionList({ vehicles }) {
  if (!vehicles.length) {
    return <EmptyState>No vehicle alerts or reviews need attention right now.</EmptyState>;
  }

  return (
    <div className="care-dashboard-list">
      {vehicles.map((vehicle) => (
        <Link className="care-dashboard-row" to={`/vehicles/${vehicle.id}`} key={vehicle.id}>
          <span className="care-dashboard-avatar">{vehicle.initials || "--"}</span>
          <span className="care-dashboard-row__main">
            <strong>{vehicle.name || "Unnamed vehicle"}</strong>
            <small>{vehicle.room ? `Room ${vehicle.room}` : statusLabel(vehicle.status)}</small>
          </span>
          <span className="care-dashboard-row__detail">
            <b>{vehicle.ncpStatusLabel || vehicle.currentIssue || "Review"}</b>
            <small>{vehicle.nextReview ? `Next review ${formatDate(vehicle.nextReview)}` : vehicle.currentIssue}</small>
          </span>
          <em className={`pill ${priorityClass(vehicle.priority)}`}>{vehicle.priority || "Low"}</em>
        </Link>
      ))}
    </div>
  );
}

function CarePlanList({ carePlans }) {
  if (!carePlans.length) {
    return <EmptyState>No campaigns are due in the next 14 days.</EmptyState>;
  }

  return (
    <div className="care-dashboard-list">
      {carePlans.map((plan) => (
        <Link className="care-dashboard-row" to={plan.vehicleId ? `/vehicles/${plan.vehicleId}#campaigns` : "/merchandising"} key={plan.id}>
          <span className="care-dashboard-avatar blue">{plan.initials || "--"}</span>
          <span className="care-dashboard-row__main">
            <strong>{plan.vehicleName || "Unnamed vehicle"}</strong>
            <small>{plan.room ? `Room ${plan.room}` : plan.title || "Negotiated campaign"}</small>
          </span>
          <span className="care-dashboard-row__detail">
            <b>{statusLabel(plan.status)}</b>
            <small>{formatDate(plan.dueDate)}</small>
          </span>
          <em className={`pill ${plan.dueStatus === "Overdue" ? "high" : plan.dueStatus === "Due today" ? "medium" : "blue"}`}>
            {plan.dueStatus || "Upcoming"}
          </em>
        </Link>
      ))}
    </div>
  );
}

function AppraisalStatusPanel({ appraisalStatuses = {} }) {
  const statuses = Object.entries(appraisalStatuses || {})
    .map(([status, total]) => ({ status, total: Number(total || 0) }))
    .filter((item) => item.total > 0)
    .sort((a, b) => b.total - a.total);
  const total = statuses.reduce((sum, item) => sum + item.total, 0);
  const completed = Number(appraisalStatuses.COMPLETED || 0);
  const completedPct = total ? Math.round((completed / total) * 100) : 0;

  return (
    <div className="care-dashboard-appraisals">
      <div className="care-dashboard-donut" style={{ "--pct": `${completedPct}%` }}>
        <strong>{completedPct}%</strong>
        <span>completed</span>
      </div>
      <div className="care-dashboard-statuses">
        {statuses.length ? statuses.slice(0, 5).map((item) => (
          <span key={item.status}>
            <b>{statusLabel(item.status)}</b>
            <em>{number(item.total)}</em>
          </span>
        )) : <EmptyState>No appraisal records yet.</EmptyState>}
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const actorId = user?.id || user?.userId || user?.uid || "";
  const [dashboard, setDashboard] = useState({
    metrics: {},
    vehicles: [],
    carePlans: [],
    appraisalStatuses: {},
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    careService.dashboard({}, actorId)
      .then((data) => {
        if (!active) return;
        setDashboard({
          metrics: data?.metrics || {},
          vehicles: Array.isArray(data?.vehicles) ? data.vehicles : [],
          carePlans: Array.isArray(data?.carePlans) ? data.carePlans : [],
          appraisalStatuses: data?.appraisalStatuses || {},
        });
        setError("");
      })
      .catch((err) => {
        if (!active) return;
        setError(err?.response?.data?.message || err?.message || "Unable to load care dashboard.");
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [actorId]);

  const metrics = useMemo(() => metricCards(dashboard.metrics), [dashboard.metrics]);

  return (
    <CareShell className="dashboard-content care-dashboard-page">
      <div className="care-dashboard-hero">
        <div>
          <span>Workspace overview</span>
          <h1>Dealership Dashboard</h1>
          <p>Live vehicle, campaign, appraisal, and staffing signals for this workspace.</p>
        </div>
        <Link className="button-primary" to="/vehicles">
          <i className="ti ti-car" /> Vehicles
        </Link>
      </div>

      {error ? <p className="form-error">{error}</p> : null}

      <div className="care-dashboard-metrics">
        {metrics.map((metric) => (
          <MetricCard metric={metric} key={metric.label} />
        ))}
      </div>

      <div className="care-dashboard-grid">
        <Panel title="Vehicles Requiring Attention">
          {loading ? <EmptyState>Loading vehicles...</EmptyState> : <VehicleAttentionList vehicles={dashboard.vehicles} />}
          <Link className="text-action align-end" to="/vehicles">Open vehicles <i className="ti ti-arrow-right" /></Link>
        </Panel>

        <Panel title="Campaigns Due Soon">
          {loading ? <EmptyState>Loading campaigns...</EmptyState> : <CarePlanList carePlans={dashboard.carePlans} />}
          <Link className="text-action align-end" to="/merchandising">Open campaigns <i className="ti ti-arrow-right" /></Link>
        </Panel>

        <Panel title="Appraisal Status">
          {loading ? <EmptyState>Loading appraisals...</EmptyState> : <AppraisalStatusPanel appraisalStatuses={dashboard.appraisalStatuses} />}
          <Link className="text-action align-end" to="/appraisals">Open appraisals <i className="ti ti-arrow-right" /></Link>
        </Panel>
      </div>
    </CareShell>
  );
}
